"use client"

import { useRef, useState } from "react"
import { Play, Volume2, VolumeX } from "lucide-react"

const CDN = "https://cdn.soku.ai/marketing/videos/tools/seedance-2-mini"
const BYTEDANCE_CDN =
  "https://lf3-static.bytednsdoc.com/obj/eden-cn/lapzild-tss/ljhwZthlaukjlkulzlp/user-upload"

type Sample = {
  label: string
  desc: string
  tag: string
  video: string
}

const samples: Sample[] = [
  {
    label: "Cinematic Landscape",
    desc: "A lone figure on an endless salt flat at dusk — natural depth, haze, and reflection.",
    tag: "Text-to-Video",
    video: `${CDN}/sample-a.mp4`,
  },
  {
    label: "Editorial Portrait",
    desc: "A suited figure on a concrete staircase — moody architectural light, smooth dolly.",
    tag: "Image-to-Video",
    video: `${CDN}/sample-b.mp4`,
  },
  {
    label: "Surreal Underwater",
    desc: "An octopus guarding a soccer ball on the seabed — fluid motion and stable physics.",
    tag: "Text-to-Video",
    video: `${CDN}/sample-c.mp4`,
  },
  {
    label: "Atmospheric Night",
    desc: "A warmly lit cabin in a snowy pine forest — falling snow with native ambient audio.",
    tag: "Native Audio",
    video: `${CDN}/sample-d.mp4`,
  },
  {
    label: "Culinary ASMR",
    desc: "Sushi rolled by gloved hands in close-up — tactile detail with synchronized sound.",
    tag: "Native Audio",
    video: `${CDN}/sample-e.mp4`,
  },
  {
    label: "Aerial Scenery",
    desc: "Hot-air balloons drifting over misty rolling hills at dawn — sweeping camera move.",
    tag: "Camera Motion",
    video: `${CDN}/sample-f.mp4`,
  },
  {
    label: "Product Commercial",
    desc: "An animated character interacting with a beverage — ad-style scene with synced audio.",
    tag: "Reference-to-Video",
    video: `${BYTEDANCE_CDN}/4uec3mlj41lce.mp4`,
  },
  {
    label: "Action Cinematic",
    desc: "A wuxia martial-arts confrontation in the rain — dynamic motion and sound design.",
    tag: "Multi-Shot",
    video: `${BYTEDANCE_CDN}/4uec3mlj40og8.mp4`,
  },
  {
    label: "Beauty & Lifestyle",
    desc: "First-person ASMR close-ups with a healing ambiance — soft light, tactile sounds.",
    tag: "Native Audio",
    video: `${BYTEDANCE_CDN}/4uec3mlj41f4z.mp4`,
  },
]

export function SeedanceMiniGallery() {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const [soundOn, setSoundOn] = useState<number | null>(null)

  const handleEnter = (i: number) => {
    const v = videoRefs.current[i]
    if (v) void v.play().catch(() => {})
  }

  const handleLeave = (i: number) => {
    const v = videoRefs.current[i]
    if (!v) return
    v.pause()
    if (soundOn !== i) v.currentTime = 0
  }

  const toggleSound = (i: number) => {
    const next = soundOn === i ? null : i
    setSoundOn(next)
    videoRefs.current.forEach((v, idx) => {
      if (!v) return
      v.muted = idx !== next
      if (idx === next) void v.play().catch(() => {})
    })
  }

  return (
    <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {samples.map((s, i) => (
        <div
          key={s.label}
          className="group overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md"
          onMouseEnter={() => handleEnter(i)}
          onMouseLeave={() => handleLeave(i)}
        >
          <div className="relative aspect-video overflow-hidden bg-black">
            <video
              ref={(el) => {
                videoRefs.current[i] = el
              }}
              src={s.video}
              muted
              loop
              playsInline
              preload="metadata"
              className="h-full w-full object-cover"
            />
            {/* Hover-to-play hint */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/15 opacity-100 transition-opacity group-hover:opacity-0">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white">
                <Play className="size-3.5" /> Hover to play
              </span>
            </div>
            {/* Tag */}
            <span className="absolute left-3 top-3 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
              {s.tag}
            </span>
            {/* Sound toggle */}
            <button
              type="button"
              aria-label={soundOn === i ? "Mute" : "Unmute"}
              className="absolute bottom-3 right-3 rounded-full bg-black/55 p-1.5 text-white/90 transition-colors hover:bg-black/75"
              onClick={() => toggleSound(i)}
            >
              {soundOn === i ? <Volume2 className="size-4" /> : <VolumeX className="size-4" />}
            </button>
          </div>
          <div className="p-4">
            <h3 className="text-sm font-semibold text-foreground">{s.label}</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">{s.desc}</p>
          </div>
        </div>
      ))}
    </div>
  )
}
